/**
 * The staging call monitor (#2224): a silent LiveKit agent that joins every
 * call while call diagnostics are on, to measure whether each speaker's audio
 * reaches the server. It is an instrument, not a person, so it must never
 * show up as a tile, a name, a "N others" count or a row in the people list.
 *
 * It is told apart by identity: the agent joins as `weco-monitor-<job id>`
 * (services/assistant core/monitor/worker.py; services/video leaves the same
 * prefix out of its participant lists). The agent kind alone is not enough:
 * the Mochi meeting twin is also an agent and is deliberately shown, with its
 * AI badge.
 */

export const CALL_MONITOR_IDENTITY_PREFIX = "weco-monitor-";
/** Data topic of the monitor's 1/s arrival signal (consumed in phase 7). */
export const CALL_MONITOR_TOPIC = "weco.monitor";

interface ParticipantLike {
  identity: string;
  isAgent?: boolean;
}

/** True only for the call monitor: an agent whose identity carries the prefix. */
export function isCallMonitor(participant: ParticipantLike | null | undefined): boolean {
  return (
    !!participant &&
    participant.isAgent === true &&
    participant.identity.startsWith(CALL_MONITOR_IDENTITY_PREFIX)
  );
}

/** The participants a person should see: everyone but the call monitor. */
export function withoutCallMonitor<P extends ParticipantLike>(participants: P[]): P[] {
  return participants.some(isCallMonitor) ? participants.filter((p) => !isCallMonitor(p)) : participants;
}

/** Track references (tiles, placeholders) minus any that belong to the call monitor. */
export function tracksWithoutCallMonitor<T extends { participant: ParticipantLike }>(tracks: T[]): T[] {
  return tracks.some((t) => isCallMonitor(t.participant))
    ? tracks.filter((t) => !isCallMonitor(t.participant))
    : tracks;
}
